import React from 'react';
import { ShieldCheck, HeartPulse, Building2 } from 'lucide-react';
import { IITH, SERIF, SANS, heroBackground } from '@/pages/landing/iithBrand';

const TRUST = [
  { icon: ShieldCheck, label: 'OTP-secured access' },
  { icon: HeartPulse, label: 'Campus clinical care' },
  { icon: Building2, label: 'Leadership & staff portals' },
];

export default function LoginBrandPanel({
  name, logo, primary, tagline, heroImage,
}) {
  const accent = primary || IITH.saffron;
  const brand = name || IITH.hospital;
  const style = heroBackground(accent, heroImage);

  return (
    <aside
      className="relative hidden lg:flex lg:w-[52%] min-h-screen flex-col justify-between overflow-hidden px-10 xl:px-14 py-12 text-white"
      style={style}
      data-testid="login-brand-panel"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div
        className="absolute top-0 left-0 right-0 h-1 z-10"
        style={{ background: `linear-gradient(90deg, ${accent}, ${IITH.gold}, ${accent})` }}
      />
      <div
        className="absolute -right-28 top-1/4 h-[480px] w-[480px] rounded-full blur-3xl opacity-25 pointer-events-none animate-[loginPulse_8s_ease-in-out_infinite]"
        style={{ background: accent }}
      />
      <div
        className="absolute -left-20 bottom-0 h-[320px] w-[320px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: IITH.gold }}
      />

      <div className="relative z-10">
        <a href="/" className="inline-flex items-center gap-3 group">
          {logo ? (
            <img
              src={logo}
              alt={brand}
              className="h-14 w-14 object-contain bg-white rounded-full p-1.5 shadow-xl"
            />
          ) : (
            <div
              className="h-14 w-14 rounded-full grid place-items-center text-white font-bold"
              style={{ background: accent, fontFamily: SERIF }}
            >
              IH
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: IITH.gold, fontFamily: SANS }}>
              {IITH.institute}
            </p>
            <p className="text-sm font-medium text-white/90" style={{ fontFamily: SANS }}>{brand}</p>
          </div>
        </a>
      </div>

      <div className="relative z-10 max-w-lg animate-[loginFade_0.8s_ease-out]">
        <p className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: IITH.gold, fontFamily: SANS }}>
          Hospital staff & campus portal
        </p>
        <h1
          className="text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight mb-4"
          style={{ fontFamily: SERIF }}
          data-testid="login-brand-name"
        >
          Care with precision.
        </h1>
        <p className="text-lg text-[#E8EEF5]/90 mb-3" style={{ fontFamily: SERIF }}>
          Access with confidence.
        </p>
        <p className="text-sm text-[#9AADBE] leading-relaxed max-w-md">
          {tagline || IITH.support}
        </p>
        <ul className="mt-10 space-y-3">
          {TRUST.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-3 text-sm text-[#E8EEF5]/85">
              <span
                className="h-9 w-9 rounded-full grid place-items-center border border-white/15 bg-white/5"
                style={{ color: accent }}
              >
                <Icon className="h-4 w-4" />
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative z-10 text-[11px] text-[#9AADBE]" style={{ fontFamily: SANS }}>
        {IITH.campus}
      </p>

      <style>{`
        @keyframes loginFade {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes loginPulse {
          0%, 100% { opacity: 0.22; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.06); }
        }
      `}</style>
    </aside>
  );
}
